import type { FC, ReactNode } from 'react'

import { useState } from 'react'
import Link from 'next/link'

import {
    Droplets,
    GlassWater,
    Info,
    Scale,
    TriangleAlert
} from 'lucide-react'

import { cn } from '@/lib/utils'

// Total water a day, from drinks AND food, as two official reference ranges.
// EFSA (2010, Scientific Opinion on Dietary Reference Values for water): adequate
// intakes of "2.0 L/day" for women and "2.5 L/day" for men, applying to moderate
// temperatures and moderate activity. US Institute of Medicine (2004): adequately
// hydrated women average "approximately 2.7 liters" and men "approximately 3.7
// liters" of total water a day. The calculator shows the span between the two.
const TOTAL_L = {
    female: { low: 2.0, high: 2.7 },
    male: { low: 2.5, high: 3.7 },
    unspecified: { low: 2.0, high: 3.7 }
} as const

// Institute of Medicine (2004): about 80 percent of total water comes from
// drinks and "the other 20 percent is derived from food".
const FROM_DRINKS = 0.8

// EFSA (2010): pregnancy adds 300 mL a day; breastfeeding about 700 mL a day
// above the adequate intake for women.
const PREGNANCY_EXTRA_L = 0.3
const BREASTFEEDING_EXTRA_L = 0.7

// NATA (2017, Fluid Replacement for the Physically Active): "not allow more than
// a 2% body mass loss" during exercise. Sweat loss (L) = mass before (kg) - mass
// after (kg) + fluid drunk (L), so 1 kg of mass lost counts as 1 L of sweat.
const MAX_LOSS_PERCENT = 2

const LB_PER_KG = 2.20462
const L_PER_US_CUP = 0.2366
const L_PER_FL_OZ = 0.02957

type Sex = keyof typeof TOTAL_L
type Stage = 'none' | 'pregnant' | 'breastfeeding'
type Unit = 'l' | 'cups' | 'oz'
type WeightUnit = 'kg' | 'lb'

const SEX_OPTIONS: { value: Sex; label: string }[] = [
    { value: 'female', label: 'Female' },
    { value: 'male', label: 'Male' },
    { value: 'unspecified', label: 'Prefer not to say' }
]

const STAGE_OPTIONS: { value: Stage; label: string }[] = [
    { value: 'none', label: 'Neither' },
    { value: 'pregnant', label: 'Pregnant' },
    { value: 'breastfeeding', label: 'Breastfeeding' }
]

const UNIT_OPTIONS: { value: Unit; label: string }[] = [
    { value: 'l', label: 'Liters' },
    { value: 'cups', label: 'US cups' },
    { value: 'oz', label: 'Fluid ounces' }
]

const chipClass = (selected: boolean): string =>
    cn(
        'rounded-full border px-4 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        selected
            ? 'border-primary bg-primary text-primary-foreground'
            : 'border-border bg-background text-foreground hover:bg-secondary'
    )

const inputClass =
    'mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-base text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring'

const formatAmount = (liters: number, unit: Unit): string => {
    if (unit === 'cups') return `${Math.round(liters / L_PER_US_CUP)} cups`
    if (unit === 'oz') return `${Math.round(liters / L_PER_FL_OZ)} fl oz`

    return `${(Math.round(liters * 10) / 10).toFixed(1)} L`
}

const formatRange = (low: number, high: number, unit: Unit): string => {
    const from = formatAmount(low, unit)
    const to = formatAmount(high, unit)
    // Show the unit once: "2.0 to 2.7 L".
    const [fromNumber] = from.split(' ')

    return from === to ? from : `${fromNumber} to ${to}`
}

const parseNumber = (value: string): number | null => {
    const parsed = Number(value.replace(',', '.'))
    return value.trim() !== '' && Number.isFinite(parsed) && parsed >= 0 ? parsed : null
}

interface ChipGroupProps<T extends string> {
    id: string
    label: string
    options: { value: T; label: string }[]
    value: T
    onChange: (value: T) => void
}

const ChipGroup = <T extends string>({ id, label, options, value, onChange }: ChipGroupProps<T>): ReactNode => (
    <div>
        <span id={id} className='block text-xs font-medium text-muted-foreground'>{label}</span>
        <div role='radiogroup' aria-labelledby={id} className='mt-2 flex flex-wrap gap-2'>
            {options.map((option) => (
                <button
                    key={option.value}
                    type='button'
                    role='radio'
                    aria-checked={value === option.value}
                    onClick={() => onChange(option.value)}
                    className={chipClass(value === option.value)}
                >
                    {option.label}
                </button>
            ))}
        </div>
    </div>
)

export const WaterCalculator: FC = (): ReactNode => {
    const [sex, setSex] = useState<Sex>('female')
    const [stage, setStage] = useState<Stage>('none')
    const [unit, setUnit] = useState<Unit>('l')

    const [weightUnit, setWeightUnit] = useState<WeightUnit>('kg')
    const [before, setBefore] = useState('')
    const [after, setAfter] = useState('')
    const [drank, setDrank] = useState('')
    const [minutes, setMinutes] = useState('60')

    const base = TOTAL_L[sex]
    const extra = sex === 'male' || stage === 'none' ? 0 : stage === 'pregnant' ? PREGNANCY_EXTRA_L : BREASTFEEDING_EXTRA_L
    const totalLow = base.low + extra
    const totalHigh = base.high + extra

    // Sweat check: needs both weights and a duration.
    const toKg = (value: number): number => (weightUnit === 'kg' ? value : value / LB_PER_KG)
    const beforeValue = parseNumber(before)
    const afterValue = parseNumber(after)
    const drankValue = parseNumber(drank) ?? 0
    const minutesValue = parseNumber(minutes)
    const sweat =
        beforeValue !== null && afterValue !== null && minutesValue && beforeValue > 0
            ? (() => {
                const lostKg = toKg(beforeValue) - toKg(afterValue)
                const lossL = lostKg + drankValue
                return {
                    lossL,
                    rate: lossL / (minutesValue / 60),
                    percent: (lostKg / toKg(beforeValue)) * 100
                }
            })()
            : null

    return (
        <section
            aria-label='Water intake calculator'
            className='my-10 overflow-hidden rounded-2xl border border-border bg-card shadow-sm'
        >
            <div className='border-b border-border bg-secondary/50 px-5 py-4 sm:px-6'>
                <div className='flex items-center gap-2'>
                    <Droplets className='size-5 shrink-0 text-primary' />
                    <span className='font-heading text-lg font-semibold tracking-tight text-foreground'>
                        Water intake calculator
                    </span>
                </div>
                <span className='mt-1 block text-sm text-muted-foreground'>
                    See how much water official guidance suggests a day, and check how much you sweat during exercise.
                </span>
            </div>

            <div className='px-5 py-5 sm:px-6'>
                <div className='flex flex-col gap-5'>
                    <ChipGroup id='water-sex' label='Sex' options={SEX_OPTIONS} value={sex} onChange={setSex} />
                    {sex !== 'male' && (
                        <ChipGroup id='water-stage' label='Pregnant or breastfeeding?' options={STAGE_OPTIONS} value={stage} onChange={setStage} />
                    )}
                    <ChipGroup id='water-unit' label='Show amounts in' options={UNIT_OPTIONS} value={unit} onChange={setUnit} />
                </div>

                <div aria-live='polite' className='mt-6 rounded-xl border border-primary/40 bg-primary/5 px-4 py-4'>
                    <span className='block text-sm font-medium text-foreground'>Total water a day, from drinks and food</span>
                    <span className='mt-1 block font-heading text-3xl font-semibold tracking-tight text-foreground'>
                        {formatRange(totalLow, totalHigh, unit)}
                    </span>
                    <span className='mt-3 flex gap-2 text-sm leading-relaxed text-foreground'>
                        <GlassWater className='mt-0.5 size-4 shrink-0 text-primary' />
                        <span>
                            About {formatRange(totalLow * FROM_DRINKS, totalHigh * FROM_DRINKS, unit)} of that from drinks, since
                            food supplies around a fifth. Water, tea, coffee, milk and other drinks all count.
                        </span>
                    </span>
                    <span className='mt-3 block text-xs leading-relaxed text-muted-foreground'>
                        The lower figure is the European Food Safety Authority&apos;s adequate intake and the higher one is
                        the US average for well-hydrated adults.
                        {extra > 0 && ` It includes the extra ${formatAmount(extra, unit)} a day suggested for ${stage === 'pregnant' ? 'pregnancy' : 'breastfeeding'}.`}{' '}
                        Both assume mild weather and moderate activity. Heat and exercise raise your needs, and thirst is a
                        good guide for most healthy adults.
                    </span>
                </div>

                <div className='mt-6 rounded-xl border border-border bg-background px-4 py-4'>
                    <span className='flex items-center gap-2 text-sm font-semibold text-foreground'>
                        <Scale className='size-4 text-primary' />
                        Exercise sweat check (optional)
                    </span>
                    <span className='mt-1 block text-xs leading-relaxed text-muted-foreground'>
                        Weigh yourself just before and just after a workout, in as little clothing as possible. Each
                        kilogram you lose is about a liter of sweat.
                    </span>

                    <div className='mt-3'>
                        <ChipGroup
                            id='water-weight-unit'
                            label='Weigh in'
                            options={[{ value: 'kg', label: 'kg' }, { value: 'lb', label: 'lb' }]}
                            value={weightUnit}
                            onChange={setWeightUnit}
                        />
                    </div>

                    <div className='mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4'>
                        <div>
                            <label htmlFor='water-before' className='block text-xs font-medium text-muted-foreground'>Weight before</label>
                            <input id='water-before' inputMode='decimal' value={before} onChange={(event) => setBefore(event.target.value)} className={inputClass} placeholder={weightUnit === 'kg' ? '70.0' : '154'} />
                        </div>
                        <div>
                            <label htmlFor='water-after' className='block text-xs font-medium text-muted-foreground'>Weight after</label>
                            <input id='water-after' inputMode='decimal' value={after} onChange={(event) => setAfter(event.target.value)} className={inputClass} placeholder={weightUnit === 'kg' ? '69.4' : '152.8'} />
                        </div>
                        <div>
                            <label htmlFor='water-drank' className='block text-xs font-medium text-muted-foreground'>Drank during (L)</label>
                            <input id='water-drank' inputMode='decimal' value={drank} onChange={(event) => setDrank(event.target.value)} className={inputClass} placeholder='0.5' />
                        </div>
                        <div>
                            <label htmlFor='water-minutes' className='block text-xs font-medium text-muted-foreground'>Minutes</label>
                            <input id='water-minutes' inputMode='numeric' value={minutes} onChange={(event) => setMinutes(event.target.value)} className={inputClass} />
                        </div>
                    </div>

                    <div aria-live='polite' className='mt-4'>
                        {!sweat ? (
                            <span className='block text-sm text-muted-foreground'>
                                Enter both weights and how long you exercised to see your sweat rate.
                            </span>
                        ) : sweat.lossL < 0 ? (
                            <span className='block text-sm text-muted-foreground'>
                                You weigh more after than before, so you drank more than you sweated. There is no need to drink
                                more than you lose.
                            </span>
                        ) : (
                            <span className='flex flex-col gap-2 text-sm text-foreground'>
                                <span>
                                    You sweated about <span className='font-semibold'>{formatAmount(sweat.lossL, unit)}</span>, a rate of{' '}
                                    <span className='font-semibold'>{formatAmount(sweat.rate, unit)} an hour</span>.
                                </span>
                                <span className={cn('flex gap-2', sweat.percent >= MAX_LOSS_PERCENT ? 'text-foreground' : 'text-muted-foreground')}>
                                    {sweat.percent >= MAX_LOSS_PERCENT && <TriangleAlert className='mt-0.5 size-4 shrink-0 text-primary' />}
                                    <span>
                                        You lost {sweat.percent.toFixed(1)}% of your body weight.{' '}
                                        {sweat.percent >= MAX_LOSS_PERCENT
                                            ? `That is above the ${MAX_LOSS_PERCENT}% that sports guidance aims to stay under, so drink more during similar sessions.`
                                            : `That is under the ${MAX_LOSS_PERCENT}% that sports guidance aims to stay under.`}{' '}
                                        Aim to replace what you lose, not more.
                                    </span>
                                </span>
                            </span>
                        )}
                    </div>
                </div>

                <div className='mt-5 flex gap-2 rounded-xl border border-border bg-secondary/40 px-4 py-3'>
                    <Info className='mt-0.5 size-4 shrink-0 text-muted-foreground' />
                    <span className='text-xs leading-relaxed text-muted-foreground'>
                        General guidance for healthy adults, not medical advice. If a doctor has told you to limit fluids,
                        for example for heart or kidney problems, follow their advice instead. Nothing you enter is stored.
                        See{' '}
                        <Link href='/nutrition' className='font-normal text-inherit underline underline-offset-2 hover:text-foreground'>
                            our guide to nutrition
                        </Link>
                        .
                    </span>
                </div>
            </div>
        </section>
    )
}

export default WaterCalculator
